import { useEffect, useState } from "react";
import { adminApi } from "../api";
import { toast } from "sonner";
import {
  KeyRound,
  Plus,
  Trash2,
  Copy,
  ToggleLeft,
  ToggleRight,
  ExternalLink,
} from "lucide-react";

export default function AdminAccessCodes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ label: "", max_uses: "", expires_at: "" });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const r = await adminApi.get("/admin/access-codes");
      setItems(r.data);
    } catch {
      toast.error("Errore caricamento codici");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        label: form.label || "",
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
        expires_at: form.expires_at
          ? new Date(form.expires_at + "T23:59:59").toISOString()
          : null,
      };
      const r = await adminApi.post("/admin/access-codes", payload);
      toast.success(`Codice ${r.data.code} generato`);
      setShowCreate(false);
      setForm({ label: "", max_uses: "", expires_at: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (item) => {
    try {
      await adminApi.patch(`/admin/access-codes/${item.id}`, {
        active: !item.active,
      });
      toast.success(item.active ? "Codice disattivato" : "Codice attivato");
      load();
    } catch {
      toast.error("Errore");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare definitivamente il codice?")) return;
    try {
      await adminApi.delete(`/admin/access-codes/${id}`);
      toast.success("Eliminato");
      load();
    } catch {
      toast.error("Errore");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code).then(
      () => toast.success("Codice copiato"),
      () => toast.error("Impossibile copiare")
    );
  };

  const copyLink = (code) => {
    const url = `${window.location.origin}/investor?code=${code}`;
    navigator.clipboard.writeText(url).then(
      () => toast.success("Link copiato"),
      () => toast.error("Impossibile copiare")
    );
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return iso;
    }
  };

  const codeStatus = (item) => {
    if (!item.active) return { label: "Disattivato", color: "text-white/40" };
    if (item.expires_at && new Date(item.expires_at) < new Date())
      return { label: "Scaduto", color: "text-red-400" };
    if (item.max_uses && item.used_count >= item.max_uses)
      return { label: "Esaurito", color: "text-red-400" };
    return { label: "Attivo", color: "text-emerald-400" };
  };

  return (
    <div data-testid="admin-access-codes-page">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#C48F56]">
        CMS
      </div>
      <div className="mt-2 flex items-center justify-between flex-wrap gap-4">
        <h1 className="font-display font-light text-5xl tracking-tightest">
          Codici Investitori
        </h1>
        <button
          onClick={() => setShowCreate(true)}
          data-testid="access-code-create-btn"
          className="inline-flex items-center gap-2 rounded-full bg-white text-black px-5 py-2.5 text-sm font-medium hover:bg-[#C48F56] transition-colors"
        >
          <Plus size={14} /> Nuovo codice
        </button>
      </div>
      <p className="mt-3 text-white/50 text-sm max-w-2xl">
        Genera codici di accesso per consentire agli investitori di entrare
        nell'area riservata senza email e password. Puoi impostare un limite di
        usi e una scadenza.
      </p>

      {/* Create form (inline) */}
      {showCreate && (
        <form
          onSubmit={handleCreate}
          className="mt-8 rounded-2xl border border-white/10 bg-[#0F0F14] p-6 space-y-4"
        >
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                Etichetta (opzionale)
              </label>
              <input
                type="text"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                data-testid="access-code-label"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#C48F56] outline-none"
                placeholder="Es. Fondo Generali Real Estate"
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                Numero massimo di usi
              </label>
              <input
                type="number"
                min="1"
                value={form.max_uses}
                onChange={(e) =>
                  setForm({ ...form, max_uses: e.target.value })
                }
                data-testid="access-code-maxuses"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#C48F56] outline-none"
                placeholder="Lascia vuoto per illimitato"
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">
                Scadenza
              </label>
              <input
                type="date"
                value={form.expires_at}
                onChange={(e) =>
                  setForm({ ...form, expires_at: e.target.value })
                }
                data-testid="access-code-expires"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#C48F56] outline-none"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={creating}
              data-testid="access-code-submit"
              className="rounded-full bg-white text-black px-5 py-2 text-sm font-medium hover:bg-[#C48F56] disabled:opacity-50 transition-colors"
            >
              {creating ? "Generazione…" : "Genera codice"}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="rounded-full border border-white/15 px-5 py-2 text-sm hover:bg-white/5"
            >
              Annulla
            </button>
          </div>
        </form>
      )}

      {/* List */}
      <div className="mt-8 rounded-2xl border border-white/10 bg-[#0F0F14] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-white/40">Caricamento…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <KeyRound size={28} className="mx-auto text-white/30" />
            <p className="mt-4 text-white/50 text-sm">
              Nessun codice ancora generato. Crea il primo per iniziare a
              invitare investitori.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 text-white/40 font-mono text-[10px] uppercase tracking-[0.2em]">
              <tr>
                <th className="text-left px-5 py-3 font-normal">Codice</th>
                <th className="text-left px-5 py-3 font-normal">Etichetta</th>
                <th className="text-left px-5 py-3 font-normal">Usi</th>
                <th className="text-left px-5 py-3 font-normal">Scadenza</th>
                <th className="text-left px-5 py-3 font-normal">Stato</th>
                <th className="text-right px-5 py-3 font-normal">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const status = codeStatus(item);
                return (
                  <tr
                    key={item.id}
                    data-testid={`access-code-row-${item.id}`}
                    className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]"
                  >
                    <td className="px-5 py-4">
                      <button
                        onClick={() => copyCode(item.code)}
                        className="font-mono text-base tracking-[0.15em] text-white hover:text-[#C48F56] inline-flex items-center gap-2"
                        title="Clicca per copiare"
                      >
                        {item.code}
                        <Copy size={12} className="opacity-60" />
                      </button>
                    </td>
                    <td className="px-5 py-4 text-white/70">
                      {item.label || "—"}
                    </td>
                    <td className="px-5 py-4 text-white/60 font-mono text-xs">
                      {item.used_count}
                      {item.max_uses ? ` / ${item.max_uses}` : ""}
                    </td>
                    <td className="px-5 py-4 text-white/60 font-mono text-xs">
                      {formatDate(item.expires_at)}
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`font-mono text-[10px] uppercase tracking-[0.2em] ${status.color}`}
                      >
                        {status.label}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="inline-flex items-center gap-3">
                        <button
                          onClick={() => copyLink(item.code)}
                          title="Copia link diretto"
                          className="text-white/50 hover:text-white"
                        >
                          <ExternalLink size={14} />
                        </button>
                        <button
                          onClick={() => toggleActive(item)}
                          title={item.active ? "Disattiva" : "Attiva"}
                          className="text-white/50 hover:text-white"
                          data-testid={`toggle-${item.id}`}
                        >
                          {item.active ? (
                            <ToggleRight size={18} className="text-emerald-400" />
                          ) : (
                            <ToggleLeft size={18} />
                          )}
                        </button>
                        <button
                          onClick={() => remove(item.id)}
                          title="Elimina"
                          className="text-white/50 hover:text-red-400"
                          data-testid={`delete-${item.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
